import type {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  DeveloperPortalSupportAgentRbacAuthorization,
  developerPortalSupportAgentReadPermission,
  developerPortalSupportAgentCreatePermission,
  developerPortalSupportAgentUpdatePermission,
  developerPortalSupportAgentDeletePermission,
} from '@internal/backstage-plugin-developer-portal-support-agent-common';
import type { Conversation, ConversationMenuItem } from '../types/chat.types';
import type {
  DeveloperPortalSupportAgentApi,
  Permissions,
} from './DeveloperPortalSupportAgentApi';

export class DeveloperPortalSupportAgentClient
  implements DeveloperPortalSupportAgentApi
{
  private readonly rbacAuth =
    new DeveloperPortalSupportAgentRbacAuthorization();

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('developer-portal-support-agent');
  }

  async checkPermissions(): Promise<Permissions> {
    const { token = '' } = await this.identityApi.getCredentials();
    return {
      read: this.rbacAuth.hasPermission(
        token,
        developerPortalSupportAgentReadPermission,
      ),
      create: this.rbacAuth.hasPermission(
        token,
        developerPortalSupportAgentCreatePermission,
      ),
      update: this.rbacAuth.hasPermission(
        token,
        developerPortalSupportAgentUpdatePermission,
      ),
      delete: this.rbacAuth.hasPermission(
        token,
        developerPortalSupportAgentDeletePermission,
      ),
    };
  }

  async listConversations(): Promise<ConversationMenuItem[]> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/conversations`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load conversation history: ${response.status} ${response.statusText}`,
      );
    }

    const data = (await response.json()) as ConversationMenuItem[];
    return Array.isArray(data) ? data : [];
  }

  async getConversation(conversationId: string): Promise<Conversation> {
    const response = await this.fetchApi.fetch(
      `${await this.baseUrl()}/conversations/${encodeURIComponent(
        conversationId,
      )}`,
      {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load conversation: ${response.status} ${response.statusText}`,
      );
    }

    const data = (await response.json()) as Conversation;

    if (!data) {
      throw new Error('Conversation response did not include a conversation');
    }

    return data;
  }
}
